import React, { useEffect, useState } from "react";
import { FiUser } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { clearWishlist } from "../../redux/wishlistSlice";
import axiosInstance from "../../utils/axiosInstance";
import ProductCard, { getProductImage } from "../ProductCard";
import "./wishlist.css";
import "../Pages/account.css";
import "../Cart/cartpage.css";
import { addToBuyCart, addToRentCart } from "../../redux/cartSlice";

/**
 * Map a raw wishlist API entry → the shape ProductCard expects.
 * The API response `item.varient` is a full variant object with prices, images, options, etc.
 */
const normalizeToProductCard = (item) => {
  const variant = item.varient || {};
  const slug =
    variant.slug ||
    variant.product?.slug ||
    variant.product?.friendly_url ||
    null;

  return {
    // --- identity ---
    id: variant.id,
    name: variant.name || variant.product?.name || "",
    slug,
    sku_code: variant.sku_code || "",
    listing_type: variant.varient_listing_type || null,
    availability: variant.availability,
    sale_stock: variant.sale_stock,
    rental_stock: variant.rental_stock,

    // --- images: keep the same shape ProductCard reads ---
    images: Array.isArray(variant.images) ? variant.images : [],

    // --- options (variant attributes) ---
    options: Array.isArray(variant.options) ? variant.options : [],

    // --- pricing ---
    prices: {
      sale_price: variant.prices?.sale_price ?? null,
      sale_offer_price: variant.prices?.sale_offer_price ?? null,
      rental_price: variant.prices?.rental_price ?? null,
    },
    discount_percentage: variant.discount_percentage ?? variant.product?.discount_percentage ?? null,

    // --- wishlist meta (carried through for removal) ---
    _wishlistId: item.id,
    _wishlistType: item.type === "rental" || item.type === "rent" ? "rent" : "buy",
  };
};

function WishListPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = localStorage.getItem("userid");
  const userName = localStorage.getItem("name") || "Customer";

  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterType, setFilterType] = useState("all");


  const buyCount = wishlistItems.filter(
    (item) => item._wishlistType === "buy"
  ).length;

  const rentCount = wishlistItems.filter(
    (item) => item._wishlistType === "rent"
  ).length;

  const filteredWishlist =
    filterType === "all"
      ? wishlistItems
      : wishlistItems.filter((item) => item._wishlistType === filterType);

  const logout = () => {
    ["token", "userid", "name", "store_id", "defaultAddress", "defaultAddressId", "saleAddress", "rentalAddress", "verifiedZipcode", "savedZipcode"].forEach((key) =>
      localStorage.removeItem(key)
    );
    dispatch(clearWishlist());
    window.dispatchEvent(new Event("authChange"));
    toast.success("Logged out");
    navigate("/");
  };

  const fetchWishlist = () => {
    if (!userId) {
      setWishlistItems([]);
      return;
    }
    setLoading(true);
    setError("");

    axiosInstance
      .get("catlog/wishlists/", { params: { user: userId } })
      .then((res) => {
        const results = Array.isArray(res.data?.results) ? res.data.results : [];
        const normalized = results
          .map(normalizeToProductCard)
          .filter((item) => item.id);
        setWishlistItems(normalized);
      })
      .catch(() => {
        setError("Failed to load wishlist. Please try again.");
        setWishlistItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchWishlist();
    // Instantly remove item from local state if removedId is present in event detail
    const handleUpdated = (e) => {
      if (e?.detail?.removedId) {
        setWishlistItems((prev) =>
          prev.filter((item) => {
            if (e.detail.removedType) {
              return !(String(item.id) === String(e.detail.removedId) && item._wishlistType === e.detail.removedType);
            }
            return String(item.id) !== String(e.detail.removedId);
          })
        );
        // Smoothly scroll to top to prevent the viewport from sticking to the footer when the page height shrinks
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        fetchWishlist();
      }
    };
    window.addEventListener("wishlistUpdated", handleUpdated);
    return () => window.removeEventListener("wishlistUpdated", handleUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /* ── card callbacks (same API ProductCard uses in listing pages) ── */
  const handleOpen = (product) => {
    if (product.slug) {
      navigate(`/product/${product.slug}`, {
        search: product.id ? `?variant=${product.id}` : "",
        state: { variantId: product.id, type: product._wishlistType },
      });
    }
  };

  const handleAdd = (e, product) => {
    e?.stopPropagation?.();
    dispatch(
      addToBuyCart({
        id: product.id,
        name: product.name,
        image: getProductImage(product),
        slug: product.slug,
        type: "buy",
        offerPrice: Number(product.prices?.sale_offer_price || product.prices?.sale_price || 0),
        sale_offer_price: product.prices?.sale_offer_price,
        sale_price: product.prices?.sale_price,
      })
    );
    toast.success("Added to cart");
  };

  const handleRentOpen = (product) => {
    // For rent items, navigate to the product detail page where ConfigureRentalModal lives
    handleOpen({ ...product, _wishlistType: "rent" });
  };

  /* ── Not logged in ── */
  if (!userId) {
    return (
      <main className="account-page template-construction">
        <div className="account-wrap">
          <section className="empty-cart-container empty-cart-simple" style={{ margin: "40px auto", width: "100%" }}>
            <div className="empty-cart-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
            <h2 className="empty-cart-simple-title">Please login to view your wishlist</h2>
            <p className="empty-cart-simple-desc">Log in to save products and access your wishlist anytime.</p>
            <div className="empty-cart-buttons-simple">
              <button className="simple-btn" type="button" onClick={() => navigate("/")}>Go Home</button>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="account-page wishlist-page template-construction">
      <div className="account-shell">
        <aside className="account-sidebar">
          <p style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <FiUser size={14} /> Account
          </p>
          <strong>{userName}</strong>
          <button type="button" onClick={() => navigate("/orders")}>Orders</button>
          <button type="button" onClick={() => navigate("/address")}>Address</button>
          <button className="active" type="button">Wishlist</button>
          <button type="button" className="logout-btn" onClick={logout}>Logout</button>
        </aside>

        <section className="account-content">
          <div className="wishlist-container" style={{ maxWidth: "100%", margin: 0, padding: 0 }}>

            {/* ── Loading ── */}
            {loading && (
              <section className="wishlist-empty">
                <h2>Loading Wishlist</h2>
                <p>Please wait while we fetch your saved products.</p>
              </section>
            )}

            {/* ── Error ── */}
            {error && (
              <section className="wishlist-empty">
                <h2>Something went wrong</h2>
                <p>{error}</p>
              </section>
            )}

            {/* ── Products grid using same ProductCard as listing pages ── */}
            {!loading && !error && wishlistItems.length > 0 && (
              <>

                <div className="wishlist-filter-bar">
                  <button
                    className={`wishlist-filter-btn ${filterType === "all" ? "active" : ""}`}
                    onClick={() => setFilterType("all")}
                  >
                    All
                  </button>

                  <button
                    className={`wishlist-filter-btn ${filterType === "buy" ? "active" : ""}`}
                    onClick={() => setFilterType("buy")}
                  >
                    Buy
                    {buyCount > 0 && (
                      <span className="wishlist-filter-count">{buyCount}</span>
                    )}
                  </button>

                  <button
                    className={`wishlist-filter-btn ${filterType === "rent" ? "active" : ""}`}
                    onClick={() => setFilterType("rent")}
                  >
                    Rent
                    {rentCount > 0 && (
                      <span className="wishlist-filter-count">{rentCount}</span>
                    )}
                  </button>
                </div>


                {filteredWishlist.length > 0 ? (
                  <div className="wishlist-pc-grid">
                    {filteredWishlist.map((item) => (
                      <ProductCard
                        key={`${item._wishlistId}-${item.id}`}
                        product={item}
                        mode={item._wishlistType || "buy"}
                        onOpen={item._wishlistType === "rent" ? handleRentOpen : handleOpen}
                        onAdd={handleAdd}
                        onEnquire={() => { }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="wishlist-filter-empty">
                    <h3>
                      No {filterType === "buy" ? "sale" : "rental"} products found
                    </h3>
                    <p>
                      {/* {filterType === "buy"
                        ? "You haven't added any sale products to your wishlist yet."
                        : "You haven't added any rental products to your wishlist yet."} */}
                      You haven't added any products to your wishlist yet.
                    </p>


                  </div>
                )}
              </>
            )}

            {/* ── Empty ── */}
            {!loading && !error && wishlistItems.length === 0 && (
              <section className="empty-cart-container empty-cart-simple" style={{ margin: "20px auto", width: "100%" }}>
                <div className="empty-cart-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </div>
                <h2 className="empty-cart-simple-title">Your wishlist is empty</h2>
                <p className="empty-cart-simple-desc">Looks like you haven't saved any products to your wishlist yet.</p>
                <div className="empty-cart-buttons-simple">
                  <button className="simple-btn" type="button" onClick={() => navigate("/buy")}>Buy Now</button>
                  <button className="simple-btn simple-btn-outline" type="button" onClick={() => navigate("/rent")}>Rent Now</button>
                </div>
              </section>
            )}

          </div>
        </section>
      </div>
    </main>
  );
}

export default WishListPage;
